import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { useLanguage } from '../../contexts/LanguageContext';

const ProjectTemplate = ({ project }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#0B1220]">
      {/* Hero Banner */}
      <div
        className="relative border-b border-[#1F2A3A] pt-32 pb-16"
        style={{
          background: `linear-gradient(to right, ${project.color}20, #0B1220, ${project.color}20)`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${project.color}33` }}
            >
              <project.icon size={32} style={{ color: project.color }} />
            </div>
            <div>
              <Badge
                className="mb-2"
                style={{
                  backgroundColor: `${project.color}33`,
                  color: project.color,
                  borderColor: `${project.color}55`,
                }}
              >
                {t('projectDetails.inRnD')}
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                {project.title}
              </h1>
            </div>
          </div>
          <p className="text-[#8A90A0] text-xl max-w-3xl">
            {project.tagline}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Overview */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('projectDetails.overview')}</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <p className="text-[#E6E8EF] text-lg leading-relaxed">
              {project.overview}
            </p>
          </div>
        </section>

        {/* Problem */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('projectDetails.theProblem')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {project.problems.map((problem, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-red-500/10 to-red-500/5 backdrop-blur-sm rounded-xl p-6 border border-red-500/30"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{problem.title}</h3>
                <p className="text-[#8A90A0] text-sm leading-relaxed">{problem.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Solution */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('projectDetails.ourSolution')}</h2>
          <div
            className="backdrop-blur-sm rounded-2xl p-8 border"
            style={{
              background: `linear-gradient(135deg, ${project.color}15 0%, ${project.color}05 100%)`,
              borderColor: `${project.color}55`,
            }}
          >
            <div className="space-y-4">
              {project.solutions.map((solution, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={20} style={{ color: project.color }} className="flex-shrink-0 mt-1" />
                  <p className="text-[#E6E8EF] leading-relaxed">{solution}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('projectDetails.technology')}</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <p className="text-[#8A90A0] leading-relaxed">
              {project.technology}
            </p>
          </div>
        </section>

        {/* Future Roadmap */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-6">{t('projectDetails.roadmap')}</h2>
          <div className="bg-[#1F2A3A]/30 backdrop-blur-sm rounded-2xl p-8 border border-[#1F2A3A]">
            <div className="space-y-4">
              {project.roadmap.map((milestone, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                    style={{ backgroundColor: `${project.color}33` }}
                  >
                    <span style={{ color: project.color }} className="font-bold text-sm">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{milestone.title}</h3>
                    <p className="text-[#8A90A0] text-sm">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center">
          <div
            className="rounded-2xl p-12 border"
            style={{
              background: `linear-gradient(to right, ${project.color}20, #0B1220, ${project.color}20)`,
              borderColor: `${project.color}55`,
            }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">{t('projectDetails.getInvolved')}</h2>
            <p className="text-[#8A90A0] mb-8 max-w-2xl mx-auto">
              {t('projectDetails.interestedText')}
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/contact')}
              className="text-white px-8 py-6 rounded-xl"
              style={{
                background: `linear-gradient(to right, ${project.color}, #7C3AED)`,
              }}
            >
              {t('projectDetails.contactUs')}
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProjectTemplate;